import type { Brand, Region } from '@/types/constants';
import type { ApiClientConfig } from '@/types/config';
import { getConfig } from '../config/config';
import { buildApiUrl, buildUrl } from './build-url';
import { INCENTIVE_GAMES_RGI } from '../reward-machine/constants';
import { GAME_PLAY_PATH_PREFIX } from './game-play-path';

export type LauncherUrlProps = {
    providerName?: string;
    region?: Region;
    brand?: Brand;
    gameName?: string;
    rgi?: string;
    ganId?: number | string | null;
    providerRef?: string;
    gameId?: string;
    gameUid?: string;
    theme?: string;
    page?: string;
    idNative?: string | null;
    idNativeXsell?: string | null;
};

export type LauncherUrlResolutionContext = {
    shouldUseSameDomainLauncher: boolean;
    isGameWindowV3Enabled: boolean;
    gamePlayPath: string;
};

const isRewardMachineGame = (rgi?: string) => {
    return rgi && rgi === INCENTIVE_GAMES_RGI;
};

/** Query-only launcher URLs need a trailing slash so CloudFront `/*` behaviors match (not `/path?…`). */
const hostForLauncherQueryUrl = (host: string, pathPrefix: string): string => {
    if (host.endsWith(`${pathPrefix}/`)) {
        return host;
    }
    if (host.endsWith(pathPrefix)) {
        return `${host}/`;
    }
    return host;
};

const resolveLauncherClient = (
    explicit?: LauncherUrlResolutionContext,
    isEmbedded?: boolean,
    rgi?: string
): ApiClientConfig => {
    const config = getConfig();
    const base = config.apis.LauncherClient;

    if (config.sameOriginGameLauncher) {
        return {
            ...base,
            protocol: '',
            host: '',
            pathPrefix: base.pathPrefix ?? GAME_PLAY_PATH_PREFIX
        };
    }

    let ctx: LauncherUrlResolutionContext | null = explicit ?? null;
    if (ctx === null && typeof window === 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires -- server-only; avoid top-level import for client bundles
        const { getServerContext } = require('@/store/server-context');
        const s = getServerContext();
        ctx = s.launcherUrlResolution;
    }

    if (ctx?.shouldUseSameDomainLauncher || !!isEmbedded) {
        const pathPrefix = isRewardMachineGame(rgi)
            ? GAME_PLAY_PATH_PREFIX
            : (ctx?.gamePlayPath ?? GAME_PLAY_PATH_PREFIX);

        return {
            ...base,
            host: config.domain,
            pathPrefix
        };
    }

    if (!ctx?.isGameWindowV3Enabled || isRewardMachineGame(rgi)) {
        return base;
    }

    return {
        ...base,
        host: base.host?.replace('launcher.', 'gw.')
    };
};

export const buildLauncherBaseUrl = (
    resolution?: LauncherUrlResolutionContext,
    isEmbedded?: boolean,
    rgi?: string
): string => {
    return buildApiUrl(resolveLauncherClient(resolution, isEmbedded, rgi));
};

export const getLauncherUrl = (
    {
        providerName,
        gameName,
        rgi,
        ganId,
        providerRef,
        region,
        brand,
        gameId,
        gameUid,
        theme,
        page,
        idNative,
        idNativeXsell
    }: LauncherUrlProps,
    resolution?: LauncherUrlResolutionContext
): string => {
    const client = resolveLauncherClient(resolution, undefined, rgi);
    const host = buildApiUrl(client);
    // Base LauncherClient has pathPrefix ''; treat empty as absent so the legacy
    // `/game-play` isn't lost, while still preferring a non-empty harness prefix.
    const pathPrefix = client.pathPrefix || resolution?.gamePlayPath || GAME_PLAY_PATH_PREFIX;

    if (gameUid && !providerName && !rgi) {
        return `${host}/${gameUid}`;
    }

    if (isRewardMachineGame(rgi)) {
        // TODO: unit test pass adjustments, talk with team for adjustments on IG
        return buildUrl(hostForLauncherQueryUrl(host, pathPrefix), {
            providerName,
            region,
            brand,
            gameName,
            rgi,
            theme,
            ...(page ? { page } : {})
        });
    }

    return buildUrl(hostForLauncherQueryUrl(host, pathPrefix), {
        providerName,
        region,
        brand,
        gameName,
        rgi,
        ganId,
        providerRef,
        gameId,
        gameUid,
        ...(idNative && { idNative: idNative }),
        ...(idNativeXsell && { idNativeXsell: idNativeXsell })
    });
};
